import { useState } from 'react'
import { Brain, Lightbulb, Heart, Sparkles, Users, ArrowRight, CheckCircle2 } from 'lucide-react'
import { Button } from '@lovable/components/ui/button'
import { Card } from '@lovable/components/ui/card'
import { EngineForm } from '@/pages/engine/Engine'
import { t } from 'i18next'

const LENSE_DEFAULT = `\nLearning substance of the tasks should be based on the provided materials/sources.
Use the source's concepts, laws, methods, definitions and examples as the primary source for all task content.
If the material is thin, you may enrich tasks with everyday situations or general human experiences but never create or imply new technical facts, laws, or domain-specific details that are not in the material.
When modifying the tasks you are also free to add new sentences, but keep the learning sustance the same.
Note the learning goals/targets, you must keep the focus on those, just adopt an approach to teach the content according to this additional guidance.
`

interface Props {
  onSubmit: (values: Partial<EngineForm>) => void
  onCancel?: () => void
}

export const LenseSelection: React.FC<Props> = ({ onSubmit, onCancel }) => {
  const lenses = [
    {
      id: 'brain-work',
      name: t('engine.lenses.brain_work_title', 'Brain Work'),
      icon: Brain,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10 hover:bg-blue-500/20',
      borderColor: 'border-blue-500/30',
      prompt:
        `Use the material's concepts, laws, and methods to create tasks that stretch analytical thinking and real-life application. Focus on reasoning, comparisons, justified choices, and navigating ambiguity
      Micro-examples of thinking patterns:
        •	explore multiple valid interpretations of the same concept
        •	apply a method in a realistic situation where trade-offs exist
        •	compare two principles and decide which fits better and why
      ` + LENSE_DEFAULT,
      description: t(
        'engine.lenses.brain_work_description',
        "Use the material's concepts, laws, and methods to create tasks that stretch analytical thinking and real-life application. Focus on reasoning, comparisons, justified choices, and navigating ambiguity.",
      ),
    },
    {
      id: 'creative-sparks',
      name: t('engine.lenses.creative_sparks_title', 'Creative Sparks'),
      icon: Lightbulb,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10 hover:bg-amber-500/20',
      borderColor: 'border-amber-500/30',
      prompt:
        `Use the material as fuel for imaginative exploration. Create tasks that encourage alternative versions, redesigned ideas, surprising combinations, and fresh perspectives rooted in the content.”
        Micro-examples of thinking patterns:
        •	reimagine a concept in a new context
        •	design an alternative approach that still follows the material
        •	merge two ideas from the content into something unexpected
    ` + LENSE_DEFAULT,
      description: t(
        'engine.lenses.creative_sparks_description',
        'Use the material as fuel for imaginative exploration. Create tasks that encourage alternative versions, redesigned ideas, surprising combinations, and fresh perspectives rooted in the content.',
      ),
    },
    {
      id: 'feel-reflect',
      name: t('engine.lenses.feel_reflect_title', 'Feel & Reflect'),
      icon: Heart,
      color: 'text-rose-500',
      bgColor: 'bg-rose-500/10 hover:bg-rose-500/20',
      borderColor: 'border-rose-500/30',
      prompt:
        `Transform the material into emotionally intelligent tasks that help players understand themselves and others at work.
        Create tasks that develop stress awareness, clear communication, prioritization skills, and mentalisation (seeing situations from another person's perspective).
        Use the material to anchor reflection in real situations where human reactions and interpersonal choices matter.
        Micro-examples of thinking patterns:
        •	explore how different people might feel or interpret the same situation
        •	consider tension between workload, priorities, and well-being
        •	reflect on possible misunderstandings in communication and how to resolve them
        •	examine how expectations, emotions, or assumptions influence decisions
      ` + LENSE_DEFAULT,
      description: t(
        'engine.lenses.feel_reflect_description',
        'Transform the material into emotionally intelligent tasks that help players understand themselves and others at work.',
      ),
    },
    {
      id: 'never-normal',
      name: t('engine.lenses.never_normal_title', 'Never Normal'),
      icon: Sparkles,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10 hover:bg-purple-500/20',
      borderColor: 'border-purple-500/30',
      prompt:
        `Build/Rebuild the game as if the user's material exists in a world undergoing continuous technological, organizational, and societal disruption.
        Assume that AI, automation, and extreme uncertainty reshape how people work, decide, coordinate, and fail.
        1. How to Transform the User's Material
        •	Treat every part of the input (theme, goals, characters, materials) as unstable — things that can break, mutate, or evolve under pressure.
        •	Identify which elements of the material would be most disrupted by rapid AI adoption, collapsing boundaries between roles, or shifting power dynamics.
        •	Reinterpret objects, constraints or characters as systems interacting with AI, not isolated entities.
        2. How to Build Tasks Under This Lens
        Every task must:
        •	Challenge a hidden assumption in the user's original material.
        •	Force players to adapt to fast-changing contexts, ambiguous information, or emerging AI behaviours.
        •	Require coordination between humans and AI, or humans supervising AI that behaves unexpectedly.
        •	Include at least one moment where players must revise their strategy because the environment shifts.
        Tasks can involve:
        •	Emerging failures of automation
        •	Reinterpretation of roles (humans as orchestrators, not executors)
        •	Ethical or trust dilemmas related to AI performance 
        •	study on emotional trust
        •	Tradeoffs between speed, accuracy, autonomy, and collaboration
        3. How to Shape Story, Goals, and Progression
        •	Stories should highlight discontinuity, unpredictability, or system-level consequences.
        •	Goals should evolve as new information appears — players should not be able to rely on a stable plan.
        •	Use narrative structures where feedback loops shift the environment (inspired by the cybernetic teamwork findings
        4. Required Thinking Patterns for the AI
        Embed at least one of these patterns in every task:
        •	Automation Breakpoint Analysis:
          “What part of the original task would fail first if AI automated 80% of it?”
        •	Human–AI Role Tension:
          “Where must humans intervene because AI lacks context, emotion, or judgment?”
        •	Skills of the Future:
          “What new skill becomes essential when old procedures no longer work?”
        •	System Ripple Effects:
          “If one element of the material changes suddenly, what chain reaction follows?”
          “How do autonomy, competence, and relatedness shift under extreme technological change—and how do tasks expose these tensions?”
        5. Tone & Experience
        •	Bold, imaginative, but grounded in plausible futures
        •	High-stakes decision-making
        •	A sense of accelerated change and ambiguity
        •	Humans and AI as co-actors, not tools and users
      ` + LENSE_DEFAULT,
      description: t(
        'engine.lenses.never_normal_description',
        'Transform the material into bold, future-oriented scenarios where technology, AI, and work conditions shift rapidly.',
      ),
    },
    {
      id: 'team-flow',
      name: t('engine.lenses.team_flow_title', 'Team Flow'),
      icon: Users,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10 hover:bg-emerald-500/20',
      borderColor: 'border-emerald-500/30',
      prompt:
        `Turn the material into collaborative challenges. Create tasks that require shared conclusions, combined viewpoints, co-creation, and team-based learning.”
        Micro-examples of thinking patterns:
        •	compare team members’ perspectives before deciding together
        •	build a joint model or shared solution using the content
        •	identify roles or contributions needed for group success
      ` + LENSE_DEFAULT,
      description: t(
        'engine.lenses.team_flow_description',
        'Turn the material into collaborative challenges. Create tasks that require shared conclusions, combined viewpoints, co-creation, and team-based learning.',
      ),
    },
    {
      id: 'skip',
      name: t('engine.lenses.skip_title', 'Skip This Step'),
      icon: ArrowRight,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted/10 hover:bg-muted/20',
      borderColor: 'border-muted/30',
      prompt: '',
      description: t(
        'engine.lenses.skip_description',
        'Continue without selecting a specific lense perspective. Use the default approach for your game.',
      ),
    },
  ]

  const [selectedLense, setSelectedLense] = useState<string | null>(null)
  const handleContinue = () => {
    if (selectedLense) {
      if (selectedLense === 'skip') {
        onSubmit({})
      } else {
        const lense = lenses.find((l) => l.id === selectedLense)
        if (lense) {
          onSubmit({ lense: lense.prompt })
        }
      }
    }
  }
  return (
    <div className='min-h-screen w-full bg-gradient-to-br from-background via-background to-muted/20'>
      <div className='container max-w-6xl mx-auto p-8 animate-fade-in'>
        {/* Header */}
        <div className='mb-12 text-center'>
          <h1 className='text-4xl font-bold text-foreground mb-4'>
            {t('engine.lenses.main_title', 'Choose Your Game Lense')}
          </h1>
          <p className='text-lg text-muted-foreground max-w-2xl mx-auto'>
            {t('engine.lenses.main_title_subtext', 'Define a perspective for your players.')}
          </p>
        </div>

        {/* Lense Cards Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8'>
          {lenses.map((lense) => {
            const Icon = lense.icon
            const isSelected = selectedLense === lense.id
            return (
              <Card
                key={lense.id}
                onClick={() => setSelectedLense(lense.id)}
                className={`relative cursor-pointer transition-all duration-300 p-6 border-2 ${
                  isSelected
                    ? `${lense.borderColor} ring-2 ring-offset-2 ring-offset-background shadow-lg scale-105`
                    : 'border-border hover:border-primary/50 hover:shadow-md'
                } ${lense.bgColor}`}
              >
                {isSelected && (
                  <div className='absolute -top-2 -right-2 w-7 h-7 bg-primary rounded-full flex items-center justify-center shadow-lg animate-scale-in'>
                    <CheckCircle2 className='w-5 h-5 text-primary-foreground' />
                  </div>
                )}
                <div className='flex flex-col items-center text-center space-y-4'>
                  <div className={`w-16 h-16 rounded-full ${lense.bgColor} flex items-center justify-center`}>
                    <Icon className={`w-8 h-8 ${lense.color}`} />
                  </div>

                  <div>
                    <h3 className='text-xl font-bold text-foreground mb-2'>{lense.name}</h3>
                    <p className='text-sm text-muted-foreground leading-relaxed'>{lense.description}</p>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>

        {/* Action Button */}
        <div className='flex items-center justify-center gap-4'>
          {onCancel && (
            <Button size='lg' variant='outline' onClick={() => onCancel()} className='w-full sm:w-auto'>
              {t('engine.goals_tasks.cancel_button', 'Cancel')}
            </Button>
          )}
          <Button
            size='lg'
            onClick={handleContinue}
            disabled={!selectedLense}
            className='min-w-[160px] bg-gradient-to-r from-primary to-primary-glow hover:opacity-90 transition-opacity shadow-lg'
          >
            {t('engine.lenses.continue_button', 'Continue')}
            <ArrowRight className='w-5 h-5 ml-2' />
          </Button>
        </div>

        {/* Helper Text */}
      </div>
    </div>
  )
}
